import { consola } from 'consola'
import { onMounted, onBeforeUnmount } from 'vue'
import { Capacitor } from '@capacitor/core'

/**
 * Composable для обработки системной кнопки "Назад"
 * Позволяет безопасно регистрировать и удалять обработчики
 */
export const useBackButton = () => {
  let backButtonHandler: (() => void) | null = null

  /**
   * Регистрирует обработчик системной кнопки "Назад"
   * @param handler - функция-обработчик
   * @param priority - приоритет обработчика (чем выше, тем раньше выполняется)
   */
  const registerBackButtonHandler = async (handler: () => void, priority: number = 0) => {
    // Только для нативных платформ
    if (!Capacitor.isNativePlatform()) {
      consola.debug('🔙 useBackButton: Skipping - not a native platform')
      return
    }
    
    consola.debug('🔙 useBackButton: Registering backButton listener with priority', priority)
    
    // Сохраняем ссылку на обработчик
    backButtonHandler = handler
    
    // Регистрируем обработчик
    const { App } = await import('@capacitor/app')
    App.addListener('backButton', backButtonHandler)
  }

  /**
   * Удаляет зарегистрированный обработчик системной кнопки "Назад"
   */
  const removeBackButtonHandler = async () => {
    // Только для нативных платформ
    if (!Capacitor.isNativePlatform()) {
      return
    }
    
    consola.debug('🔙 useBackButton: Removing backButton listener')
    
    if (backButtonHandler) {
      const { App } = await import('@capacitor/app')
      App.removeListener('backButton', backButtonHandler)
      backButtonHandler = null
    }
  }

  /**
   * Автоматическая регистрация и удаление обработчика в хуках жизненного цикла
   * @param handler - функция-обработчик
   * @param priority - приоритет обработчика
   */
  const useBackButtonHandler = (handler: () => void, priority: number = 0) => {
    onMounted(() => {
      registerBackButtonHandler(handler, priority)
    })

    onBeforeUnmount(() => {
      removeBackButtonHandler()
    })
  }

  /**
   * Получает текущий приоритет обработчика
   */
  const getCurrentPriority = () => {
    return backButtonHandler ? 1 : 0
  }

  return {
    registerBackButtonHandler,
    removeBackButtonHandler,
    useBackButtonHandler,
    getCurrentPriority,
  }
}
